// The per-member migration of existing plaintext (#1545; encryption spec
// docs/plans/2026-09-02-next-steps-e2e-encryption.md, section 6.7, "Migration
// of existing data (per member, client-driven)").
//
// A member who enrols usually already has notes. Enrolment seals what they
// write from now on; this seals what they wrote before. After enrolment, and on
// every Hub open while the bundle has no `migratedAt`, it fetches each row that
// still has no envelope, re-writes it through the ORDINARY write endpoints in
// batches of twenty, and — once a fetch comes back with nothing left in the
// clear — POSTs `/migrated` once.
//
// WHY IT NEEDS NO BOOKKEEPING OF ITS OWN. The API nulls a row's plaintext in the
// same transaction that stores its envelope (spec 6.6), so a migrated row stops
// matching the "still plaintext" filter the moment it is written. That single
// property is what makes this resumable: there is no cursor to persist, no
// local list of what has been done, and no way to write a row twice in a way
// that matters. Killing the app mid-run and reopening the Hub simply collects
// less than it did the first time. Nothing here creates a row — every write is
// a PATCH or a keyed PUT — so a resume cannot duplicate anything either.
//
// WHY IT READS EVERYTHING BACK RATHER THAN TRUSTING ITS OWN WRITES. The
// `/migrated` endpoint counts rows server-side and answers 409 if any remain, so
// the client asserting "I think I am done" is worth nothing. The loop below
// therefore ends on a FETCH that returns no plaintext, not on a write that
// succeeded — which is also the only way to notice rows written by another
// device, or a row this build could not seal.
//
// WHAT IT IS NOT. It is not a drain of the member's corpus into a local cache,
// and it holds nothing: a row is read, sealed by the ordinary write hook and
// forgotten. It writes none of what it reads into the `courses` store, so the
// Hub and the reader stay usable, and correct, while it runs. The one store
// dispatch it makes is `courses/fetchLessonOptions` — the lesson list, which
// the Hub loads for its own tagging dropdown anyway, and which is the only way
// to enumerate blank answers (see `collectBlanks`).
import { UNRECOVERABLE_FIELD } from '@/constants/courses-encryption';
import * as transport from '@/utils/courses-annotations-transport';
import { ANNOTATION_TYPE } from '@/utils/courses-annotations-transport';
import { cameFromEnvelope } from '@/utils/courses-annotations-read-path';
import { SEALED_FIELDS } from '@/utils/courses-encryption/sealed-writes';
import { outbox } from '@/utils/courses-autosave/outbox';

/** Spec 6.7: "Batches of 20 with a progress line in the Hub header". */
export const MIGRATION_BATCH_SIZE = 20;

/**
 * How many collect-and-write rounds one call may run. Two is the ordinary
 * course — one to write, one to see nothing left and post `/migrated`. The
 * third is there for the rows a concurrent write added between them, and the
 * loop leaves early the moment a round moves nothing, so an offline device
 * costs one round, not three.
 */
export const MIGRATION_MAX_PASSES = 3;

/** A stop for the note pager, so a server that always answers a cursor cannot spin. */
export const MIGRATION_MAX_PAGES = 200;

export const MIGRATION_OUTCOME = Object.freeze({
  /** `/migrated` was accepted: nothing of this member's is in the clear. */
  MIGRATED: 'migrated',
  /** Rows remain. Not an error — the next Hub open picks up from here. */
  IN_PROGRESS: 'in-progress',
  /** A precondition said no; `reason` says which. Nothing was fetched. */
  SKIPPED: 'skipped',
  /** The run could not be completed. The next Hub open tries again. */
  ERROR: 'error',
});

export const MIGRATION_SKIP_REASON = Object.freeze({
  /** The API does not consider this member enrolled, so nothing may be sealed. */
  NOT_ENROLLED: 'not-enrolled',
  /** Enrolled elsewhere; this device holds no key and cannot seal anything. */
  NO_KEY: 'no-key',
  /** The bundle already carries `migratedAt`, or this session already set it. */
  ALREADY_MIGRATED: 'already-migrated',
  NO_MEMBER: 'no-member',
  /**
   * An edit queued before enrolment has not been sent yet. Re-sealing a row now
   * would move its revision and make that edit stale, and the member would get
   * their pre-edit text back. The drain runs on launch, resume and reconnect;
   * the next Hub open or highlight save tries this again.
   */
  OUTBOX_PENDING: 'outbox-pending',
});

/**
 * One run per member per session, and never two at once. Enrolment keeps the
 * same kind of memo for the same reason: the Hub's `ionViewWillEnter` fires on
 * every visit, and a member walking in and out must not start a second drain
 * over the first one's rows.
 *
 * @type {Map<number, { migrated: boolean, inFlight: Promise<object> | null }>}
 */
const memos = new Map();

/** Test seam: forget every memo, as a fresh launch would. */
export function resetMigrationStateForTests() {
  memos.clear();
}

function memoFor(memberId) {
  if (!memos.has(memberId)) memos.set(memberId, { migrated: false, inFlight: null });
  return memos.get(memberId);
}

const skipped = reason => ({ outcome: MIGRATION_OUTCOME.SKIPPED, reason });

/**
 * A row that still holds its content in the clear, and so is this module's work.
 *
 * The `payload` test alone is NOT enough, and getting this wrong is fatal rather
 * than merely wasteful. The read path strips `payload` from every row it opens
 * (#1542), so an already-sealed row reaches here looking field-for-field like a
 * plaintext one — a collect pass would never come back empty, `/migrated` would
 * never be posted, and the member's whole corpus would be re-encrypted on every
 * Hub open, for ever. `cameFromEnvelope` is the read path's own word for how the
 * row arrived, which is the only thing that separates the two.
 *
 * An `unreadable` row is excluded by the same test: it was sealed, this device
 * could not open it, and re-writing it from the fields that survived would
 * replace its payload with a fraction of what it held.
 */
const isPlaintext = row =>
  !cameFromEnvelope(row) && (row?.payload === null || row?.payload === undefined);

/**
 * The content fields of one row, with an absent column stated as `null`.
 *
 * This coercion is the whole reason a migration write differs from an ordinary
 * one. The sealing hook drops `undefined` BEFORE it counts the content fields
 * against `REQUIRED_ON_UPDATE`, so a column the API omitted rather than sent as
 * `null` would make the re-write look like a partial patch and be refused. For
 * a plaintext row an absent column IS null, so saying so loses nothing — and
 * `null` survives the split, where `undefined` does not.
 *
 * The field list is `SEALED_FIELDS`, NOT `REQUIRED_ON_UPDATE`. They differ for
 * one entity and it matters here: a note's `REQUIRED_ON_UPDATE` is `['body']`
 * alone, because nothing in this app has ever written a note `title`. That is
 * true of the patches this app authors and false of the rows it is migrating —
 * a title written by some earlier client is real data, and the API nulls every
 * plaintext column of a row it stores an envelope for. Sealing `body` alone
 * would throw that title away. Section 6.4 puts `title` inside the envelope,
 * and `SEALED_FIELDS` is that list.
 *
 * @param {Record<string, unknown>} row
 * @param {readonly string[]} fields
 */
function contentOf(row, fields) {
  const content = {};
  fields.forEach(field => {
    content[field] = row[field] ?? null;
  });
  // The shell marker (rotation-walk.js `shellFor`) is not a column of any
  // type, and is the one thing a shell says.
  if (row[UNRECOVERABLE_FIELD] === true) content[UNRECOVERABLE_FIELD] = true;
  return content;
}

/**
 * How each kind of row is written back. Ordinary endpoints, ordinary bodies:
 * the sealing hook turns each of these into an envelope on the way out, which
 * is what keeps this module ignorant of the crypto entirely.
 */
const WRITE = Object.freeze({
  [ANNOTATION_TYPE.NOTE]: (auth, row) =>
    transport.updateNote(auth, row.id, contentOf(row, SEALED_FIELDS[ANNOTATION_TYPE.NOTE])),
  [ANNOTATION_TYPE.HIGHLIGHT]: (auth, row) =>
    transport.updateHighlight(
      auth,
      row.id,
      contentOf(row, SEALED_FIELDS[ANNOTATION_TYPE.HIGHLIGHT]),
    ),
  [ANNOTATION_TYPE.TAG]: (auth, row) =>
    transport.updateTag(auth, row.id, contentOf(row, SEALED_FIELDS[ANNOTATION_TYPE.TAG])),
  // A blank answer is a PUT keyed on program/lesson/block, not a patch, so the
  // key travels with the content.
  //
  // The only arm that does NOT go through `contentOf`, on purpose. `contentOf`
  // exists to stop an absent column reaching the hook as `undefined`, where
  // `REQUIRED_ON_UPDATE` would read the write as a partial patch — and a blank
  // answer is the one entity the hook does not run that check on, because a PUT
  // has no shape to be short of. What the hook does instead is treat an empty
  // answer as a DELETE (decision 30), which is the right end for a legacy row
  // holding the `''` the API used to upsert: the row goes, the plaintext goes
  // with it, and the server's count is satisfied. Passing `row.answer` through
  // `contentOf` would turn `undefined` into `null` and reach the same delete by
  // a longer road, so this says what it means rather than borrowing a helper
  // whose reason does not apply.
  [ANNOTATION_TYPE.BLANK]: (auth, row, scope) =>
    transport.upsertBlankAnswer(auth, { ...scope, answer: row.answer }),
});

/**
 * Re-write one row through its ordinary endpoint (#1694).
 *
 * The whole crypto story is that there isn't one here: the sealing hook seals
 * on the way out, under whichever generation the store says a write must use.
 * That is what lets a migration (plaintext to MK) and a rotation's re-seal
 * (MK1 to MK2) be the same call with a different set of rows.
 *
 * @param {object} auth
 * @param {{ type: string, row: object, scope?: object }} entry
 * @returns {Promise<unknown>}
 */
export function rewriteRow(auth, { type, row, scope }) {
  const write = WRITE[type];
  if (!write) throw new TypeError(`rewriteRow: nothing writes a "${type}"`);
  return write(auth, row, scope);
}

/**
 * The reader views whose annotations belong to this member. Notes and
 * highlights are filtered server-side by audience, so a leader's leader-view
 * rows are invisible to a student-view read and would be left in the clear for
 * ever. A member who is not a leader has one view and pays for one read.
 *
 * @param {object} store
 * @returns {string[]}
 */
/*
 * ⚠️ BOTH VIEWS, ALWAYS. This used to ask `courses/isLeaderGuideUser`, which
 * reads the course-access summary — a value that is simply not loaded on a
 * launch that went Me → Devices → Sign out without opening a lesson. On that
 * path a leader's highlights made in Leader view (audience LEADER) were never
 * fetched, the server counted them on the old key, and the rotation stalled
 * on "1 highlight, not reachable from this device" (2026-09-10).
 *
 * Asking for the leader view costs a member without leader access nothing:
 * the API resolves the view down to student for them, and the collectors key
 * their maps by row id, so the same rows twice are the same rows once.
 */
function viewsToDrain() {
  return ['student', 'leader'];
}

/**
 * Every note of the member, across views and pages.
 *
 * Two pagings exist on `GET /notes`: an enrolled member gets `{ items,
 * nextCursor }` and a plaintext one gets `{ data, total }` by `page`. A member
 * being migrated is enrolled, so the cursor is the live path — but the page
 * shape is handled too, because the moment between the enrolment 201 and the
 * API seeing this member as enrolled is exactly when this runs.
 *
 * @param {object} auth
 * @param {string[]} views
 * @param {Map<string, object>} into keyed by row id, so two views cannot
 *   collect the same row twice
 */
const notePageRows = data =>
  (Array.isArray(data?.items) && data.items) || (Array.isArray(data?.data) && data.data) || [];

async function collectNotesForView(auth, view, into) {
  let cursor = null;
  let page = 1;
  for (let request = 0; request < MIGRATION_MAX_PAGES; request += 1) {
    // eslint-disable-next-line no-await-in-loop
    const data = await transport.fetchAllNotes(auth, cursor ? { view, cursor } : { view, page });
    notePageRows(data).forEach(row => {
      if (row?.id) into.set(row.id, row);
    });
    cursor = data?.nextCursor ?? null;
    // Three ways this ends, one per line: another cursor to follow, the cursor
    // pager exhausted, or the paged shape out of rows.
    if (cursor) continue;
    if (Array.isArray(data?.items)) return;
    if (notePageRows(data).length === 0) return;
    page += 1;
  }
}

async function collectNotes(auth, views, into) {
  for (const view of views) {
    // eslint-disable-next-line no-await-in-loop
    await collectNotesForView(auth, view, into);
  }
}

/**
 * Every blank answer of the member. There is no member-wide list for these —
 * the only route is `GET /lessons/:slug/blank-answers` — so the lesson list the
 * Hub already loads for its tagging dropdown is what makes them enumerable.
 * Unlike notes and highlights, the route is not scoped by view.
 *
 * @param {object} store
 * @param {object} auth
 * @param {Map<string, object>} into
 */
async function collectBlanks(store, auth, into = []) {
  let lessons = store.state.courses?.lessonOptions;
  if (!Array.isArray(lessons) || lessons.length === 0) {
    // Not fatal, and not silent: with no lesson list there is nothing to ask
    // for blank answers about, so the run seals everything else and `/migrated`
    // refuses on the server's own count if any were missed.
    await store.dispatch('courses/fetchLessonOptions').catch(error => {
      console.warn('[CoursesE2EE] The lesson list could not be loaded for the migration:', error);
    });
    lessons = store.state.courses?.lessonOptions || [];
  }
  for (let i = 0; i < lessons.length; i += MIGRATION_BATCH_SIZE) {
    const batch = lessons.slice(i, i + MIGRATION_BATCH_SIZE);
    // eslint-disable-next-line no-await-in-loop
    const answered = await Promise.all(
      batch.map(async lesson => {
        const programSlug = lesson.programSlug;
        // One lesson that will not answer must not cost the whole run the rows
        // it could have sealed. The lesson's own answers stay plaintext, the
        // server's count still sees them, and the next Hub open asks again.
        const rows = await transport
          .fetchBlankAnswers(auth, { slug: lesson.slug, programSlug })
          .catch(error => {
            console.warn(
              `[CoursesE2EE] Blank answers for ${lesson.slug} could not be read:`,
              error,
            );
            return [];
          });
        // The ROW is kept, never copied. `cameFromEnvelope` is a
        // non-enumerable mark on the object the read path handed back, so
        // `{ ...row }` would strip exactly the flag that tells an opened
        // envelope from a plaintext row — and every sealed blank would then
        // read as work to do, for ever, with `/migrated` never reachable.
        // The scope travels beside the row instead; `programSlug` and
        // `lessonSlug` are NOT NULL columns the API sends anyway, and the
        // lesson they were fetched under is the fallback.
        return rows.map(row => ({
          row,
          scope: {
            programSlug: row.programSlug ?? programSlug,
            lessonSlug: row.lessonSlug ?? lesson.slug,
            blockId: row.blockId,
          },
        }));
      }),
    );
    answered.flat().forEach(entry => into.push(entry));
  }
}

/**
 * One pass over everything the member has, keeping only what is still in the
 * clear, in the order section 6.4 lists the entities.
 *
 * @returns {Promise<Array<{ type: string, row: object }>>}
 */
async function collectPlaintext(store, auth) {
  return (await collectRows(store, auth)).filter(entry => isPlaintext(entry.row));
}

/**
 * Every annotation the member has, whatever state it is in (#1694).
 *
 * Split out of `collectPlaintext` so rotation's re-seal can ask the same
 * question with a different filter. The enumeration is the hard part — notes
 * page two different ways, tags are read unscoped so neither part's are
 * missed, and blank answers have to be walked lesson by lesson because their
 * write is keyed on program/lesson/block — and having it twice would mean
 * finding out twice, the second time by a member's rows being missed.
 *
 * THE ROWS ARE HANDED BACK AS THE READ PATH RETURNED THEM, never copied.
 * `cameFromEnvelope` is a non-enumerable mark, so `{ ...row }` would strip
 * exactly the flag that tells an opened envelope from a plaintext row.
 *
 * @returns {Promise<Array<{ type: string, row: object, scope?: object }>>}
 */
export async function collectRows(store, auth) {
  const views = viewsToDrain(store);

  const notes = new Map();
  await collectNotes(auth, views, notes);

  const highlights = new Map();
  for (const view of views) {
    // eslint-disable-next-line no-await-in-loop
    const rows = await transport.fetchAllHighlights(auth, { view });
    (Array.isArray(rows) ? rows : []).forEach(row => {
      if (row?.id) highlights.set(row.id, row);
    });
  }

  // Every row here is the member's own — the API filters by creator and there
  // are no global tags — and the list is member-wide, so this walk sees all of
  // them. There is no scoped read left to get wrong.
  const tagRows = await transport.fetchTags(auth, {});
  const tags = new Map((Array.isArray(tagRows) ? tagRows : []).map(row => [row.id, row]));

  // Blank answers come back as `{ row, scope }` rather than bare rows: their
  // write is keyed on program/lesson/block, and the key must not be spread onto
  // the row (see `collectBlanks`).
  const blanks = [];
  await collectBlanks(store, auth, blanks);

  const byId = [
    [ANNOTATION_TYPE.NOTE, notes],
    [ANNOTATION_TYPE.HIGHLIGHT, highlights],
    [ANNOTATION_TYPE.TAG, tags],
  ];
  return [
    ...byId.flatMap(([type, rows]) => [...rows.values()].map(row => ({ type, row }))),
    ...blanks.map(entry => ({
      type: ANNOTATION_TYPE.BLANK,
      row: entry.row,
      scope: entry.scope,
    })),
  ];
}

/**
 * Re-write the collected rows, twenty at a time, reporting progress after each
 * batch. A row that fails does not stop the rest: it stays plaintext, the next
 * collect finds it again, and `/migrated` will not be called while it is there.
 *
 * @returns {Promise<number>} how many rows were sealed
 */
async function writeInBatches({ auth, entries, onProgress }) {
  let done = 0;
  for (let i = 0; i < entries.length; i += MIGRATION_BATCH_SIZE) {
    const batch = entries.slice(i, i + MIGRATION_BATCH_SIZE);
    // eslint-disable-next-line no-await-in-loop
    const results = await Promise.allSettled(batch.map(entry => rewriteRow(auth, entry)));
    results.forEach(result => {
      if (result.status === 'fulfilled') done += 1;
      else console.warn('[CoursesE2EE] A row could not be migrated:', result.reason);
    });
    onProgress(done);
  }
  return done;
}

/**
 * Whether this member has a write the app has not managed to send yet. Never
 * throws: a Preferences read that fails is not a reason to fail a page open,
 * and answering "no" only means the walk runs, which is what it did before.
 *
 * @param {number} memberId
 * @returns {Promise<boolean>}
 */
async function hasUnsentEdits(memberId) {
  try {
    return (await outbox.read(memberId)).length > 0;
  } catch (error) {
    return false;
  }
}

/**
 * Seal whatever this member still has in the clear, if there is any and this
 * device may do it.
 *
 * Never rejects: the caller is a page open, and a Hub must not fail to render
 * because a migration could not finish. Every answer comes back as an outcome.
 *
 * @param {object} args
 * @param {object} args.store the root Vuex store
 * @returns {Promise<{ outcome: string, reason?: string, migrated?: number, remaining?: number }>}
 */
export async function migratePlaintextIfNeeded({ store } = {}) {
  if (!store) throw new TypeError('migratePlaintextIfNeeded needs the root Vuex store');
  // NOT gated on `courses-e2ee`, deliberately, where enrolment is.
  //
  // Spec 6.9: "Rollback means 'stop enrolling new members'. Enrolled members
  // stay encrypted and keep working." A member only ever reaches this code by
  // already being enrolled and holding a key, which is exactly the population a
  // rollback is meant to keep working — and their writes are already sealed,
  // because the sealing hook keys off key material and not the flag either.
  // Gating here would strand a half-migrated member with rows in the clear and
  // no way to finish, which is the one state the rollback must not create.
  if (store.getters['courses-devices/enrolled'] !== true) {
    return skipped(MIGRATION_SKIP_REASON.NOT_ENROLLED);
  }
  if (store.getters['courses-devices/migratedAt']) {
    return skipped(MIGRATION_SKIP_REASON.ALREADY_MIGRATED);
  }
  // Enrolled elsewhere, no key here: every write would be refused, and the
  // approval path is what this member needs, not a drain.
  if (!store.getters['courses-keys/keyMaterial']) return skipped(MIGRATION_SKIP_REASON.NO_KEY);

  const memberId = store.getters['courses-keys/memberId'];
  if (!Number.isInteger(memberId) || memberId <= 0) {
    return skipped(MIGRATION_SKIP_REASON.NO_MEMBER);
  }
  const memo = memoFor(memberId);
  if (memo.migrated) return skipped(MIGRATION_SKIP_REASON.ALREADY_MIGRATED);
  if (memo.inFlight) return memo.inFlight;

  // ⚠️ NOT WHILE THE OUTBOX STILL HAS SOMETHING TO SEND, and this is about the
  // member's words, not about tidiness.
  //
  // The walk re-writes each row through the ordinary write endpoints, and the
  // API increments `revision` on every accepted write — so a re-seal MOVES THE
  // REVISION WITHOUT CHANGING A WORD. A queued edit from before enrolment names
  // the revision it read; if the walk gets there first, that edit is refused as
  // stale on the next drain and yields, and what the member gets back is their
  // own pre-edit text, merely encrypted. The conflict rule is right to yield —
  // it cannot tell our own walk's bump from a second device's edit — so the
  // answer is not to let the two collide.
  //
  // Waiting costs nothing: the drain runs at launch, on resume and when the
  // network returns, and the next Hub open or highlight save starts the
  // migration again. A member with a permanently stuck entry keeps their rows
  // in the clear a while longer, which is the lesser of the two harms.
  if (await hasUnsentEdits(memberId)) return skipped(MIGRATION_SKIP_REASON.OUTBOX_PENDING);

  memo.inFlight = run({ store, memo, memberId }).finally(() => {
    memo.inFlight = null;
  });
  return memo.inFlight;
}

async function run({ store, memo, memberId }) {
  const auth = {
    token: store.state.user?.token,
    memberId: store.state.profile?.id ?? memberId,
  };
  const setMigration = patch => store.commit('courses-devices/setMigration', patch);
  let migrated = 0;
  let remaining = 0;

  try {
    for (let pass = 0; pass < MIGRATION_MAX_PASSES; pass += 1) {
      // eslint-disable-next-line no-await-in-loop
      const entries = await collectPlaintext(store, auth);
      remaining = entries.length;
      if (entries.length === 0) {
        // The API's own answer, not this client's opinion: nothing of this
        // member's is in the clear, so the stamp may be set.
        // eslint-disable-next-line no-await-in-loop
        await store.dispatch('courses-devices/markMigrated');
        memo.migrated = true;
        return { outcome: MIGRATION_OUTCOME.MIGRATED, migrated, remaining: 0 };
      }
      setMigration({ running: true, done: 0, total: entries.length });
      // eslint-disable-next-line no-await-in-loop
      const done = await writeInBatches({
        auth,
        entries,
        onProgress: count => setMigration({ done: count }),
      });
      migrated += done;
      remaining = entries.length - done;
      // Nothing moved — offline, refused, or a row this build cannot seal.
      // Stop rather than hammer; the next Hub open resumes from here.
      if (done === 0) break;
    }
    return { outcome: MIGRATION_OUTCOME.IN_PROGRESS, migrated, remaining };
  } catch (error) {
    console.warn('[CoursesE2EE] The plaintext migration could not finish:', error);
    return { outcome: MIGRATION_OUTCOME.ERROR, migrated, remaining };
  } finally {
    setMigration({ running: false, done: 0, total: 0 });
  }
}
